/* eslint-disable max-lines -- Pre-existing violation */
import {
  Button,
  ButtonGroup,
  Checkbox,
  Dialog,
  DialogFooter,
  EmptyStatus,
  Flag,
  Flags,
  Form,
  FormButton,
  FormField,
  HelperText,
  IconButton,
  Segment,
  Separator,
  TextInput,
  useFormContext,
  type FormControl,
} from 'platform/components';
import {Box, Grid, Heading, HStack, Show, Spinner, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';
import {z} from 'zod';

import {useState} from 'react';
import {useWatch} from 'react-hook-form';

import {isNil, isNotNil, omit, uniqBy} from 'ramda';

import type {FeatureResponseBody} from '@dms/api/new-vehicle';
import {getNewVehicleEnumLabel} from '@dms/features/new-vehicle';

import {useCatalogueVatRate} from '../hooks/useCatalogueVatRate';
import {useFeatureLibrarySearch} from '../hooks/useFeatureLibrarySearch';
import {inclusionFlag, joinParts, optionalText, t} from '../utils/catalogueUi';
import {emptyEquipmentDraft, type EquipmentDraft} from '../utils/equipmentDraft';
import {optionalMoneyInput, optionalPriceError, priceInput} from '../utils/moneyInput';
import {removeDraftRecord, trimPriceRows, type NewModelDraft} from '../utils/newModelDraft';
import {chosenTrimPriceIssues, chosenTrimPrices} from '../utils/offeredTrimRows';
import {DraftRecordList, type DraftRecordListRow} from './DraftRecordList';
import {GrossPriceText} from './GrossPriceText';
import {NewEquipmentFlow} from './NewEquipmentFlow';
import {PriceWithGross} from './PriceWithGross';
import {TrimPriceFields} from './TrimPriceFields';
import {TrimPrices} from './TrimPrices';

const packageDialogSchema = z
  .object({
    name: z.string().trim().min(1),
    factoryCode: optionalText,
    description: optionalText.pipe(z.string().min(1)),
    trimPrices: z.record(z.string(), optionalText.optional()),
    costNet: optionalMoneyInput,
    memberFeatureIds: z.array(z.string()).min(1),
    applicableTrimIds: z.array(z.string()).min(1),
  })
  .superRefine((values, context) =>
    chosenTrimPriceIssues(values.applicableTrimIds, values.trimPrices).forEach((issue) =>
      context.addIssue(issue)
    )
  )
  .transform((values) => ({
    ...values,
    trimPrices: chosenTrimPrices(values.applicableTrimIds, values.trimPrices),
  }));
type PackageDialogInput = z.input<typeof packageDialogSchema>;
type PackageDialogValues = z.output<typeof packageDialogSchema>;
type PackageSaveValues = PackageDialogValues & {
  packageOnlyFeatureIds: string[];
  changedPackageOnlyFeatureIds: string[];
  createdEquipment: NewModelDraft['equipment'];
};
type Inclusion = 'STANDARD' | 'OPTIONAL';
const WIZARD_FEATURE_KINDS: FeatureResponseBody['kind'][] = ['option', 'wheel', 'accessory'];
type EquipmentCandidate =
  | {kind: 'draft'; key: string; equipment: NewModelDraft['equipment'][number]}
  | {kind: 'library'; key: string; feature: FeatureResponseBody};
type EquipmentEditor =
  {kind: 'new'} | {kind: 'edit'; id: string} | {kind: 'attach'; trimId: string};

type Props = {
  step: 8 | 9;
  draft: NewModelDraft;
  canViewCost: boolean;
  currency: string;
  isLocked: boolean;
  goTo: (step: number) => void;
  update: (draft: NewModelDraft) => void;
};

function equipmentTypeForFeature(feature: FeatureResponseBody): EquipmentDraft['equipmentType'] {
  return match(feature)
    .returnType<EquipmentDraft['equipmentType']>()
    .with({kind: 'wheel'}, () => 'wheel')
    .with({fitStage: 'dealer'}, () => 'dealer-accessory')
    .otherwise(() => 'factory-option');
}

function optionalStartPrice(draft: NewModelDraft, featureId: string) {
  return (
    draft.attachments.find(
      (row) =>
        row.featureId === featureId &&
        row.inclusion === 'OPTIONAL' &&
        Number(priceInput.safeParse(row.priceNet).data ?? 0) > 0
    )?.priceNet ?? ''
  );
}

function toLibraryCandidate(feature: FeatureResponseBody): EquipmentCandidate {
  return {kind: 'library', key: `library:${feature.id}`, feature};
}

function libraryEquipment(feature: FeatureResponseBody): NewModelDraft['equipment'][number] {
  return {
    ...omit(['trimPrices'], emptyEquipmentDraft),
    id: crypto.randomUUID(),
    libraryId: feature.id,
    name: feature.name,
    category: feature.category,
    factoryCode: feature.factoryCode ?? '',
    equipmentType: equipmentTypeForFeature(feature),
    standaloneOrderable: feature.standaloneOrderable,
  };
}

function candidateItem(candidate: EquipmentCandidate) {
  return candidate.kind === 'draft' ? candidate.equipment : candidate.feature;
}

function tabLabel(picks: ReadonlyMap<string, Inclusion>, label: string, inclusion: Inclusion) {
  const pickCount = [...picks.values()].filter((picked) => picked === inclusion).length;
  return joinParts([label, pickCount > 0 && pickCount.toString()]);
}

function EquipmentPickerDialog(props: {
  trim: NewModelDraft['trims'][number];
  draft: NewModelDraft;
  currency: string;
  onClose: () => void;
  onSave: (draft: NewModelDraft) => void;
}) {
  const [tab, setTab] = useState<Inclusion>('STANDARD');
  const [query, setQuery] = useState('');
  const term = query.trim();
  const library = useFeatureLibrarySearch(term);
  const vatRate = useCatalogueVatRate();
  const [picks, setPicks] = useState<ReadonlyMap<string, Inclusion>>(() => new Map());
  const [prices, setPrices] = useState<Partial<Record<string, string>>>({});
  const priceOf = (candidate: EquipmentCandidate) =>
    prices[candidate.key] ??
    (candidate.kind === 'draft' ? optionalStartPrice(props.draft, candidate.equipment.id) : '');
  const [pickedLibrary, setPickedLibrary] = useState<FeatureResponseBody[]>([]);
  const representedLibraryIds = new Set(
    props.draft.equipment.flatMap((row) => (row.libraryId ? [row.libraryId] : []))
  );
  const attachedIds = new Set(
    props.draft.attachments
      .filter((row) => row.trimId === props.trim.id)
      .map((row) => row.featureId)
  );
  const lowerTerm = term.toLocaleLowerCase();
  const matchesQuery = (name: string) => name.toLocaleLowerCase().includes(lowerTerm);
  const draftCandidates = props.draft.equipment
    .filter((equipment) => !attachedIds.has(equipment.id))
    .map((equipment) => ({kind: 'draft' as const, key: `draft:${equipment.id}`, equipment}));
  const libraryMatches = library.features.filter(
    (feature) =>
      !feature.archived &&
      WIZARD_FEATURE_KINDS.includes(feature.kind) &&
      !representedLibraryIds.has(feature.id)
  );
  const candidates = [
    ...draftCandidates.filter((candidate) => matchesQuery(candidate.equipment.name)),
    ...uniqBy(
      ({id}) => id,
      [...pickedLibrary.filter((feature) => matchesQuery(feature.name)), ...libraryMatches]
    ).map(toLibraryCandidate),
  ].filter((candidate) => tab === 'STANDARD' || candidateItem(candidate).standaloneOrderable);
  const pickable = [...draftCandidates, ...pickedLibrary.map(toLibraryCandidate)];
  const invalidPriceNames = pickable
    .filter(
      (candidate) =>
        picks.get(candidate.key) === 'OPTIONAL' && isNotNil(optionalPriceError(priceOf(candidate)))
    )
    .map((candidate) => candidateItem(candidate).name);
  const submit = () => {
    const addedEquipment: NewModelDraft['equipment'] = [];
    const attachments: NewModelDraft['attachments'] = [];
    picks.forEach((pickedInclusion, key) => {
      const candidate = pickable.find((row) => row.key === key);
      if (isNil(candidate)) {
        return;
      }
      const equipment =
        candidate.kind === 'library' ? libraryEquipment(candidate.feature) : candidate.equipment;
      if (candidate.kind === 'library') {
        addedEquipment.push(equipment);
      }
      const inclusion = equipment.standaloneOrderable ? pickedInclusion : 'STANDARD';
      attachments.push({
        id: crypto.randomUUID(),
        trimId: props.trim.id,
        featureId: equipment.id,
        inclusion,
        priceNet:
          inclusion === 'OPTIONAL' ? (priceInput.safeParse(priceOf(candidate)).data ?? '') : '',
      });
    });
    props.onSave({
      ...props.draft,
      equipment: [...props.draft.equipment, ...addedEquipment],
      attachments: [...props.draft.attachments, ...attachments],
    });
  };
  return (
    <Dialog
      isOpen
      size="large"
      withAdditionalFooter
      title={t('entity.newVehicle.authoring.addEquipmentToTrim', 'Add equipment to {trim}', {
        trim: props.trim.name,
      })}
      onClose={props.onClose}
    >
      <VStack spacing={4}>
        <Segment<Inclusion>
          label={t('entity.newVehicle.authoring.inclusion', 'Inclusion')}
          value={tab}
          options={[
            {
              value: 'STANDARD',
              label: tabLabel(
                picks,
                t('entity.newVehicle.authoring.standardIncluded', 'Standard (included)'),
                'STANDARD'
              ),
            },
            {
              value: 'OPTIONAL',
              label: tabLabel(
                picks,
                t('entity.newVehicle.authoring.optionalPaid', 'Optional (paid add-on)'),
                'OPTIONAL'
              ),
            },
          ]}
          onChange={setTab}
        />
        <Show when={tab === 'OPTIONAL'}>
          <Text size="small" color="secondary">
            {t(
              'entity.newVehicle.authoring.packageOnlyHiddenOptional',
              'Package-only items are hidden when adding as optional.'
            )}
          </Text>
        </Show>
        <TextInput
          label={t('entity.newVehicle.authoring.searchEquipment', 'Search equipment')}
          placeholder={t(
            'entity.newVehicle.authoring.searchEquipmentPlaceholder',
            'Search equipment…'
          )}
          helperText={t(
            'entity.newVehicle.catalogue.typeToSearchLibrary',
            'Type a name or code to search the library'
          )}
          value={query}
          onChange={(value) => setQuery(value ?? '')}
        />
        <VStack spacing={0}>
          {candidates.map((candidate, index) => {
            const {name, category} = candidateItem(candidate);
            const price = priceOf(candidate);
            const picked = picks.get(candidate.key);
            const otherTabPick = isNotNil(picked) && picked !== tab ? picked : null;
            return (
              <Box key={candidate.key}>
                <Show when={index > 0}>
                  <Separator spacing={0} />
                </Show>
                <Box paddingVertical={3}>
                  <HStack align="center" justify="space-between" spacing={4}>
                    <HStack align="center" spacing={3} grow={1}>
                      <Checkbox
                        aria-label={name}
                        value={picked === tab}
                        isDisabled={isNotNil(otherTabPick)}
                        onChange={(checked) => {
                          if (checked && candidate.kind === 'library') {
                            setPickedLibrary((current) =>
                              uniqBy(({id}) => id, [...current, candidate.feature])
                            );
                          }
                          setPicks((current) => {
                            const next = new Map(current);
                            if (checked) {
                              next.set(candidate.key, tab);
                            } else {
                              next.delete(candidate.key);
                            }
                            return next;
                          });
                        }}
                      />
                      <Text size="small">{name}</Text>
                      <Flag
                        label={getNewVehicleEnumLabel('featureCategory', category, t)}
                        size="small"
                        isSubtle
                        colorScheme="neutral"
                      />
                      <Show when={otherTabPick}>
                        {(otherInclusion) => (
                          <Flag {...inclusionFlag(otherInclusion)} size="small" />
                        )}
                      </Show>
                    </HStack>
                    {match({tab, isPicked: picked === tab})
                      .with({tab: 'STANDARD'}, () => (
                        <Text size="small" color="secondary">
                          {t('entity.newVehicle.authoring.includedShort', 'Incl.')}
                        </Text>
                      ))
                      .with({isPicked: true}, () => (
                        <VStack spacing={1}>
                          <TextInput
                            aria-label={t(
                              'entity.newVehicle.catalogue.priceOnTrimNet',
                              'Price of {feature} on {trim}, net',
                              {feature: name, trim: props.trim.name}
                            )}
                            value={price}
                            suffix={props.currency}
                            errorMessage={optionalPriceError(price)}
                            onChange={(value) =>
                              setPrices((current) => ({...current, [candidate.key]: value ?? ''}))
                            }
                          />
                          <GrossPriceText net={price} currency={props.currency} vatRate={vatRate} />
                        </VStack>
                      ))
                      .otherwise(() => (
                        <Show when={priceInput.safeParse(price).data}>
                          {(amount) => (
                            <PriceWithGross
                              amount={amount}
                              currency={props.currency}
                              vatRate={vatRate}
                            />
                          )}
                        </Show>
                      ))}
                  </HStack>
                </Box>
              </Box>
            );
          })}
          <Show when={library.isSearching}>
            <Spinner size="small" />
          </Show>
          <Show when={candidates.length === 0 && !library.isSearching}>
            <Text size="small" color="secondary">
              {t('entity.newVehicle.authoring.noEquipmentAvailable', 'No equipment available.')}
            </Text>
          </Show>
        </VStack>
      </VStack>
      <DialogFooter data-testid="newVehicleCatalogue-equipmentPicker">
        <HStack align="center" justify="flex-end" spacing={4}>
          <Show when={invalidPriceNames.length > 0}>
            <Text size="small" color="danger">
              {t(
                'entity.newVehicle.authoring.fixOptionalPrices',
                'Fix the Optional price of {items}.',
                {items: invalidPriceNames.join(', ')}
              )}
            </Text>
          </Show>
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={t('general.actions.cancel', 'Cancel')}
              onClick={props.onClose}
            />
            <Button
              variant="primary"
              title={t('entity.newVehicle.catalogue.addEquipment', 'Add equipment')}
              suffix={picks.size > 0 ? `(${picks.size})` : undefined}
              isDisabled={picks.size === 0 || invalidPriceNames.length > 0}
              onClick={submit}
            />
          </ButtonGroup>
        </HStack>
      </DialogFooter>
    </Dialog>
  );
}

function EquipmentSection(props: {
  trim: NewModelDraft['trims'][number];
  draft: NewModelDraft;
  currency: string;
  isLocked: boolean;
  onAdd: () => void;
  onEdit: (equipmentId: string) => void;
  onChangeInclusion: (attachmentId: string, inclusion: Inclusion) => void;
  onRemove: (attachmentId: string) => void;
}) {
  const vatRate = useCatalogueVatRate();
  const rows = props.draft.attachments
    .filter((attachment) => attachment.trimId === props.trim.id)
    .flatMap((attachment) => {
      const equipment = props.draft.equipment.find((row) => row.id === attachment.featureId);
      return equipment ? [{attachment, equipment}] : [];
    });
  const standardCount = rows.filter((row) => row.attachment.inclusion === 'STANDARD').length;
  const optionalCount = rows.filter((row) => row.attachment.inclusion === 'OPTIONAL').length;
  return (
    <VStack spacing={3}>
      <HStack align="center" justify="space-between" spacing={4} wrap>
        <HStack align="center" spacing={3} wrap>
          <Heading size={4}>{props.trim.name}</Heading>
          <Flags
            size="small"
            isSubtle
            colorScheme="neutral"
            flags={[
              {
                label: t('entity.newVehicle.authoring.standardCount', '{count} standard', {
                  count: standardCount,
                }),
              },
              {
                label: t('entity.newVehicle.authoring.optionalCount', '{count} optional', {
                  count: optionalCount,
                }),
              },
            ]}
          />
        </HStack>
        <Button
          variant="secondary"
          leftIcon="content/add"
          title={t('entity.newVehicle.catalogue.addEquipment', 'Add equipment')}
          aria-label={t(
            'entity.newVehicle.authoring.addEquipmentToTrim',
            'Add equipment to {trim}',
            {trim: props.trim.name}
          )}
          isDisabled={props.isLocked}
          onClick={props.onAdd}
        />
      </HStack>
      {rows.length === 0 ? (
        <Text size="small" color="secondary">
          {t('entity.newVehicle.authoring.noEquipmentAttached', 'No equipment attached yet.')}
        </Text>
      ) : (
        <VStack spacing={0}>
          {rows.map(({attachment, equipment}, index) => (
            <Box key={attachment.id}>
              <Show when={index > 0}>
                <Separator spacing={0} />
              </Show>
              <Box paddingVertical={3}>
                <HStack align="center" justify="space-between" spacing={4} wrap>
                  <VStack spacing={1}>
                    <HStack align="center" spacing={2}>
                      <Text alternative>{equipment.name}</Text>
                      <Show
                        when={
                          props.draft.attachments.find(
                            (candidate) => candidate.featureId === equipment.id
                          )?.id === attachment.id
                        }
                      >
                        <Button
                          variant="link"
                          title={t(
                            'entity.newVehicle.authoring.editEquipmentNamed',
                            'Edit {name}',
                            {name: equipment.name}
                          )}
                          isDisabled={props.isLocked}
                          onClick={() => props.onEdit(equipment.id)}
                        />
                      </Show>
                    </HStack>
                    <HStack align="center" spacing={2} wrap>
                      <Text size="small" color="secondary">
                        {getNewVehicleEnumLabel('featureCategory', equipment.category, t)}
                      </Text>
                      <Text size="small" color="secondary">
                        ·
                      </Text>
                      <Text size="small" color="secondary">
                        {attachment.inclusion === 'STANDARD'
                          ? t('entity.newVehicle.authoring.standardShort', 'Standard')
                          : t('entity.newVehicle.authoring.optionalShort', 'Optional')}
                      </Text>
                      <Text size="small" color="secondary">
                        ·
                      </Text>
                      {match({inclusion: attachment.inclusion, price: attachment.priceNet})
                        .with({inclusion: 'STANDARD'}, () => (
                          <Text size="small" color="secondary">
                            {t('entity.newVehicle.authoring.included', 'Included')}
                          </Text>
                        ))
                        .with({price: ''}, () => (
                          <Text size="small" color="danger">
                            {t('entity.newVehicle.authoring.noPriceYet', 'No price yet')}
                          </Text>
                        ))
                        .otherwise(({price}) => (
                          <PriceWithGross
                            amount={price}
                            currency={props.currency}
                            vatRate={vatRate}
                            align="left"
                          />
                        ))}
                      <Show when={!equipment.standaloneOrderable}>
                        <Flag
                          label={t('entity.newVehicle.authoring.packageOnly', 'Package-only')}
                          size="small"
                          isSubtle
                          colorScheme="neutral"
                        />
                      </Show>
                    </HStack>
                  </VStack>
                  <HStack align="center" spacing={3}>
                    <VStack spacing={1}>
                      <Segment<Inclusion>
                        label={t(
                          'entity.newVehicle.authoring.equipmentInclusionForItem',
                          'Inclusion for {name}',
                          {name: equipment.name}
                        )}
                        value={attachment.inclusion}
                        options={[
                          {
                            value: 'STANDARD',
                            label: t('entity.newVehicle.authoring.standardShort', 'Standard'),
                            isDisabled: props.isLocked || !equipment.standaloneOrderable,
                          },
                          {
                            value: 'OPTIONAL',
                            label: t('entity.newVehicle.authoring.optionalShort', 'Optional'),
                            isDisabled: props.isLocked || !equipment.standaloneOrderable,
                          },
                        ]}
                        onChange={(value) => props.onChangeInclusion(attachment.id, value)}
                      />
                      <Show when={!equipment.standaloneOrderable}>
                        <Text size="small" color="secondary">
                          {t(
                            'entity.newVehicle.authoring.standardOnly',
                            'Standard equipment only. Add this item to a package to sell it as part of that package.'
                          )}
                        </Text>
                      </Show>
                    </VStack>
                    <IconButton
                      icon="action/delete"
                      severity="danger"
                      aria-label={t(
                        'entity.newVehicle.authoring.removeEquipmentFromTrim',
                        'Remove {name} from {trim}',
                        {name: equipment.name, trim: props.trim.name}
                      )}
                      isDisabled={props.isLocked}
                      onClick={() => props.onRemove(attachment.id)}
                    />
                  </HStack>
                </HStack>
              </Box>
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
}

function PackageDialogFields(props: {
  control: FormControl<PackageDialogInput>;
  draft: NewModelDraft;
  canViewCost: boolean;
  packageOnlyIds: string[];
  onTogglePackageOnly: (id: string, checked: boolean) => void;
  onNewItem: () => void;
}) {
  const form = useFormContext<PackageDialogValues>();
  form.register('memberFeatureIds');
  const memberIds = useWatch({control: props.control, name: 'memberFeatureIds'});
  const [name, selectedTrimIds] = useWatch({
    control: props.control,
    name: ['name', 'applicableTrimIds'],
  });
  const toggleMember = (id: string, checked: boolean) => {
    const values = form.getValues('memberFeatureIds');
    form.setValue(
      'memberFeatureIds',
      checked ? [...values, id] : values.filter((value) => value !== id),
      {shouldDirty: true, shouldValidate: form.formState.isSubmitted}
    );
  };
  return (
    <VStack spacing={4}>
      <Grid columns={[1, 1, 2]} spacing={4}>
        <FormField
          control={props.control}
          name="name"
          type="text"
          label={t('general.labels.name', 'Name')}
          isRequired
        />
        <FormField
          control={props.control}
          name="factoryCode"
          type="text"
          label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
        />
      </Grid>
      <FormField
        control={props.control}
        name="description"
        type="textarea"
        label={t('general.labels.description', 'Description')}
        isRequired
      />
      <VStack spacing={2}>
        <Text alternative>{t('entity.newVehicle.authoring.packageMembers', 'Members')}</Text>
        <Text size="small" color="secondary">
          {t(
            'entity.newVehicle.authoring.packageMembersHelp',
            'Choose the equipment included in this package. Mark items as package-only when they should not be available separately.'
          )}
        </Text>
        <Grid columns={[2]} spacing={4}>
          <Text size="small" alternative>
            {t('entity.newVehicle.authoring.memberEquipment', 'Equipment')}
          </Text>
          <HStack justify="flex-end">
            <Text size="small" alternative>
              {t('entity.newVehicle.authoring.packageOnly', 'Package-only')}
            </Text>
          </HStack>
        </Grid>
        {props.draft.equipment.map((equipment) => (
          <Grid key={equipment.id} columns={[2]} spacing={4}>
            <HStack align="center" spacing={3}>
              <Checkbox
                aria-label={equipment.name}
                value={memberIds.includes(equipment.id)}
                onChange={(checked) => toggleMember(equipment.id, checked)}
              />
              <Text size="small">{equipment.name}</Text>
              <Flag
                label={getNewVehicleEnumLabel('featureCategory', equipment.category, t)}
                size="small"
                isSubtle
                colorScheme="neutral"
              />
            </HStack>
            <HStack justify="flex-end">
              <Checkbox
                aria-label={t(
                  'entity.newVehicle.authoring.packageOnlyEquipment',
                  'Package-only {name}',
                  {name: equipment.name}
                )}
                label={
                  equipment.libraryId !== null
                    ? t('entity.newVehicle.authoring.packageOnly', 'Package-only')
                    : undefined
                }
                helperText={
                  equipment.libraryId !== null
                    ? t(
                        'entity.newVehicle.authoring.libraryTaxonomyReadOnly',
                        'Library items keep their own setting'
                      )
                    : undefined
                }
                value={
                  equipment.libraryId !== null
                    ? !equipment.standaloneOrderable
                    : props.packageOnlyIds.includes(equipment.id)
                }
                isDisabled={!memberIds.includes(equipment.id) || equipment.libraryId !== null}
                onChange={(checked) => props.onTogglePackageOnly(equipment.id, checked)}
              />
            </HStack>
          </Grid>
        ))}
        <Show when={isNotNil(form.formState.errors.memberFeatureIds)}>
          <HelperText
            errorMessage={t('general.validations.chooseAtLeastOne', 'Choose at least one item')}
          />
        </Show>
        <HStack>
          <Button
            variant="link"
            leftIcon="content/add"
            title={t('entity.newVehicle.authoring.createEquipmentItem', 'New equipment item')}
            onClick={props.onNewItem}
          />
        </HStack>
      </VStack>
      <TrimPriceFields
        control={props.control}
        trimIdsField="applicableTrimIds"
        priceField={(trimId) => `trimPrices.${trimId}`}
        costField="costNet"
        trimsLabel={t('entity.newVehicle.authoring.applicableTrims', 'Applicable trims')}
        trimOptions={props.draft.trims.map((trim) => ({value: trim.id, label: trim.name}))}
        trims={props.draft.trims}
        selectedTrimIds={selectedTrimIds}
        itemName={name}
        canViewCost={props.canViewCost}
      />
    </VStack>
  );
}

function PackageDialog(props: {
  row?: NewModelDraft['packages'][number];
  draft: NewModelDraft;
  canViewCost: boolean;
  onClose: () => void;
  onSave: (values: PackageSaveValues) => Promise<void>;
}) {
  const isEditing = props.row !== undefined;
  const [packageOnlyIds, setPackageOnlyIds] = useState(
    props.row?.memberFeatureIds.filter(
      (id) => props.draft.equipment.find((row) => row.id === id)?.standaloneOrderable === false
    ) ?? []
  );
  const [changedPackageOnlyIds, setChangedPackageOnlyIds] = useState<string[]>([]);
  // Items created here reach the draft only as members of the saved package, never as orphans.
  const [createdEquipment, setCreatedEquipment] = useState<NewModelDraft['equipment']>([]);
  const [isCreatingEquipment, setIsCreatingEquipment] = useState(false);
  const togglePackageOnly = (id: string, checked: boolean) => {
    setPackageOnlyIds((ids) => (checked ? [...ids, id] : ids.filter((value) => value !== id)));
    setChangedPackageOnlyIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  };
  return (
    <Dialog
      isOpen
      size="large"
      title={
        isEditing
          ? t('entity.newVehicle.authoring.editPackage', 'Edit package')
          : t('entity.newVehicle.catalogue.addPackage', 'Add package')
      }
      // Every open Dialog closes on Escape; keep the package open while the item flow handles it.
      onClose={isCreatingEquipment ? () => undefined : props.onClose}
    >
      <Form<PackageDialogInput>
        experimentalZodSchema={packageDialogSchema}
        defaultValues={{
          name: props.row?.name ?? '',
          factoryCode: props.row?.factoryCode ?? '',
          description: props.row?.description ?? '',
          trimPrices: props.row?.trimPrices ?? {},
          costNet: props.row?.costNet ?? '',
          memberFeatureIds: props.row?.memberFeatureIds ?? [],
          applicableTrimIds: props.row?.applicableTrimIds ?? [],
        }}
        onSubmit={(values) =>
          props.onSave({
            ...packageDialogSchema.parse(values),
            packageOnlyFeatureIds: packageOnlyIds,
            changedPackageOnlyFeatureIds: changedPackageOnlyIds,
            createdEquipment,
          })
        }
      >
        {(control, form) => (
          <VStack spacing={5}>
            <PackageDialogFields
              control={control}
              draft={{...props.draft, equipment: [...props.draft.equipment, ...createdEquipment]}}
              canViewCost={props.canViewCost}
              packageOnlyIds={packageOnlyIds}
              onTogglePackageOnly={togglePackageOnly}
              onNewItem={() => setIsCreatingEquipment(true)}
            />
            <Show when={isCreatingEquipment}>
              <NewEquipmentFlow
                canViewCost={props.canViewCost}
                trims={[]}
                scopeLabel={
                  props.draft.year.catalogueName ||
                  t('entity.newVehicle.authoring.currentDraft', 'Current model-year draft')
                }
                ownershipMessage={t(
                  'entity.newVehicle.authoring.packageOnlyHelp',
                  'Sold through packages, never as a standalone option. It may be included as standard equipment. The price belongs to the package.'
                )}
                initialValues={{...emptyEquipmentDraft, standaloneOrderable: false}}
                onClose={() => setIsCreatingEquipment(false)}
                onSave={(values) => {
                  const row = {
                    ...omit(['trimPrices'], values),
                    id: crypto.randomUUID(),
                    libraryId: null,
                  };
                  setCreatedEquipment((rows) => [...rows, row]);
                  form.setValue(
                    'memberFeatureIds',
                    [...form.getValues('memberFeatureIds'), row.id],
                    {shouldDirty: true, shouldValidate: form.formState.isSubmitted}
                  );
                  togglePackageOnly(row.id, true);
                  setIsCreatingEquipment(false);
                }}
              />
            </Show>
            <HStack justify="flex-end" spacing={3}>
              <Button
                variant="secondary"
                title={t('general.actions.cancel', 'Cancel')}
                onClick={props.onClose}
              />
              <FormButton
                control={control}
                type="submit"
                variant="primary"
                title={
                  isEditing
                    ? t('general.actions.saveChanges', 'Save changes')
                    : t('entity.newVehicle.catalogue.addPackage', 'Add package')
                }
              />
            </HStack>
          </VStack>
        )}
      </Form>
    </Dialog>
  );
}

export function NewModelEquipmentPackageSteps(props: Props) {
  const [editor, setEditor] = useState<EquipmentEditor | null>(null);
  const [packageId, setPackageId] = useState<string | 'new' | null>(null);
  const vatRate = useCatalogueVatRate();
  const selectedEquipment =
    editor?.kind === 'edit' ? props.draft.equipment.find((row) => row.id === editor.id) : undefined;
  const selectedAttachments = props.draft.attachments.filter(
    (attachment) => attachment.featureId === selectedEquipment?.id
  );
  const selectedTrim =
    editor?.kind === 'attach'
      ? props.draft.trims.find((row) => row.id === editor.trimId)
      : undefined;
  const selectedPackage =
    packageId && packageId !== 'new'
      ? props.draft.packages.find((row) => row.id === packageId)
      : undefined;
  const packageRows: DraftRecordListRow[] = props.draft.packages.map((row) => ({
    id: row.id,
    name: row.name,
    subtitle: joinParts([
      t('entity.newVehicle.authoring.itemsCount', '{count} items', {
        count: row.memberFeatureIds.length,
      }),
      t('entity.newVehicle.authoring.trimsCount', '{count} trims', {
        count: row.applicableTrimIds.length,
      }),
    ]),
    values: [
      <TrimPrices
        key="price"
        rows={trimPriceRows(row.applicableTrimIds, row.trimPrices)}
        trims={props.draft.trims}
        currency={props.currency}
        vatRate={vatRate}
        canViewCost={false}
      />,
    ],
  }));
  const saveEquipment = (values: EquipmentDraft) => {
    const id = selectedEquipment?.id ?? crypto.randomUUID();
    const row = {
      ...omit(['trimPrices'], values),
      id,
      libraryId: selectedEquipment?.libraryId ?? null,
    };
    props.update({
      ...props.draft,
      equipment: selectedEquipment
        ? props.draft.equipment.map((equipment) => (equipment.id === id ? row : equipment))
        : [...props.draft.equipment, row],
      attachments: [
        ...props.draft.attachments.filter((attachment) => attachment.featureId !== id),
        ...values.trimIds.map((trimId) => {
          const existing = props.draft.attachments.find(
            (attachment) => attachment.featureId === id && attachment.trimId === trimId
          );
          return {
            id: existing?.id ?? crypto.randomUUID(),
            trimId,
            featureId: id,
            inclusion: values.standaloneOrderable
              ? (existing?.inclusion ?? values.inclusion)
              : ('STANDARD' as const),
            priceNet: values.trimPrices[trimId] ?? '',
          };
        }),
      ],
    });
    setEditor(null);
  };
  if (props.step === 8) {
    return (
      <VStack spacing={5}>
        <HStack>
          <Button
            variant="secondary"
            leftIcon="content/add"
            title={t('entity.newVehicle.authoring.createEquipmentItem', 'New equipment item')}
            isDisabled={props.isLocked}
            onClick={() => setEditor({kind: 'new'})}
          />
        </HStack>
        {props.draft.trims.length === 0 ? (
          <EmptyStatus
            headline={t('entity.newVehicle.authoring.addTrimsFirst', 'Add trim levels first')}
            subheadline={t(
              'entity.newVehicle.authoring.equipmentNeedsTrims',
              'Equipment is attached to trim levels.'
            )}
            action={{
              variant: 'secondary',
              title: t('entity.newVehicle.authoring.goToTrims', 'Go to Trim levels'),
              onClick: () => props.goTo(5),
            }}
          />
        ) : (
          props.draft.trims.map((trim, index) => (
            <Box key={trim.id}>
              <Show when={index > 0}>
                <Separator />
              </Show>
              <EquipmentSection
                trim={trim}
                draft={props.draft}
                currency={props.currency}
                isLocked={props.isLocked}
                onAdd={() => setEditor({kind: 'attach', trimId: trim.id})}
                onEdit={(id) => setEditor({kind: 'edit', id})}
                onChangeInclusion={(attachmentId, inclusion) =>
                  props.update({
                    ...props.draft,
                    attachments: props.draft.attachments.map((attachment) =>
                      attachment.id === attachmentId
                        ? {
                            ...attachment,
                            inclusion,
                            priceNet:
                              inclusion === 'OPTIONAL' && attachment.priceNet === ''
                                ? optionalStartPrice(props.draft, attachment.featureId)
                                : attachment.priceNet,
                          }
                        : attachment
                    ),
                  })
                }
                onRemove={(attachmentId) =>
                  props.update({
                    ...props.draft,
                    attachments: props.draft.attachments.filter(
                      (attachment) => attachment.id !== attachmentId
                    ),
                  })
                }
              />
            </Box>
          ))
        )}
        <Show when={editor?.kind === 'new' || editor?.kind === 'edit'}>
          <NewEquipmentFlow
            key={editor?.kind === 'edit' ? editor.id : 'new'}
            isIntentLocked={props.isLocked}
            isSaveDisabled={props.isLocked}
            canViewCost={props.canViewCost}
            trims={props.draft.trims}
            scopeLabel={
              props.draft.year.catalogueName ||
              t('entity.newVehicle.authoring.currentDraft', 'Current model-year draft')
            }
            ownershipMessage={t(
              'entity.newVehicle.authoring.draftEquipmentOwnership',
              'This adds equipment to this model only. Select trims to attach it now; the library item is created together with the catalogue draft.'
            )}
            existingInclusions={
              selectedEquipment
                ? Object.fromEntries(
                    selectedAttachments.map(({trimId, inclusion}) => [trimId, inclusion])
                  )
                : undefined
            }
            isIdentityLocked={isNotNil(selectedEquipment?.libraryId)}
            initialValues={
              selectedEquipment
                ? {
                    ...selectedEquipment,
                    trimIds: selectedAttachments.map(({trimId}) => trimId),
                    trimPrices: Object.fromEntries(
                      selectedAttachments.map(({trimId, priceNet}) => [trimId, priceNet])
                    ),
                  }
                : undefined
            }
            onClose={() => setEditor(null)}
            onSave={saveEquipment}
          />
        </Show>
        <Show when={selectedTrim !== undefined}>
          <EquipmentPickerDialog
            trim={selectedTrim!}
            draft={props.draft}
            currency={props.currency}
            onClose={() => setEditor(null)}
            onSave={(draft) => {
              props.update(draft);
              setEditor(null);
            }}
          />
        </Show>
      </VStack>
    );
  }
  return (
    <VStack spacing={4}>
      <DraftRecordList
        title={t('entity.newVehicle.catalogue.packages', 'Packages')}
        addLabel={t('entity.newVehicle.catalogue.addPackage', 'Add package')}
        emptyHeadline={t('entity.newVehicle.authoring.noPackages', 'No packages yet')}
        emptySubheadline={t(
          'entity.newVehicle.authoring.packagesOptionalPerTrim',
          'Packages are optional. Add one to sell several items together, at its own price on each trim.'
        )}
        rows={packageRows}
        isLocked={props.isLocked}
        onAdd={() => setPackageId('new')}
        onEdit={setPackageId}
        onRemove={(id) => props.update(removeDraftRecord(props.draft, 'packages', id))}
      />
      <Text size="small" color="secondary">
        {t(
          'entity.newVehicle.authoring.packagePriceHelp',
          'A package has its own price; member prices are not added together.'
        )}
      </Text>
      <Show when={packageId !== null}>
        <PackageDialog
          row={selectedPackage}
          draft={props.draft}
          canViewCost={props.canViewCost}
          onClose={() => setPackageId(null)}
          onSave={(values) => {
            const id = selectedPackage?.id ?? crypto.randomUUID();
            const {
              packageOnlyFeatureIds,
              changedPackageOnlyFeatureIds,
              createdEquipment,
              ...rowValues
            } = values;
            const row = {...rowValues, id};
            const memberIds = new Set(values.memberFeatureIds);
            const packageOnlyIds = new Set(packageOnlyFeatureIds);
            const changedPackageOnlyIds = new Set(changedPackageOnlyFeatureIds);
            props.update({
              ...props.draft,
              equipment: [
                ...props.draft.equipment,
                ...createdEquipment.filter((row) => memberIds.has(row.id)),
              ].map((equipment) => {
                if (
                  !memberIds.has(equipment.id) ||
                  !changedPackageOnlyIds.has(equipment.id) ||
                  equipment.libraryId !== null
                ) {
                  return equipment;
                }
                return {
                  ...equipment,
                  standaloneOrderable: !packageOnlyIds.has(equipment.id),
                };
              }),
              attachments: props.draft.attachments.map((attachment) =>
                memberIds.has(attachment.featureId) && packageOnlyIds.has(attachment.featureId)
                  ? {...attachment, inclusion: 'STANDARD'}
                  : attachment
              ),
              packages: selectedPackage
                ? props.draft.packages.map((stored) => (stored.id === id ? row : stored))
                : [...props.draft.packages, row],
            });
            setPackageId(null);
            return Promise.resolve();
          }}
        />
      </Show>
    </VStack>
  );
}
